/**
 * iter548 · Reviewer Guide Banner
 *
 * Coach-mark banner that renders at the top of the current page
 * whenever `?reviewer-guide=<key>` is in the URL AND the signed-in
 * user is the Apple Review reviewer account.
 *
 * Fired from `ReviewerQuickAccessPanel.jsx`: tapping "Report Content"
 * navigates to `/network/ozzie-albies?reviewer-guide=report` — this
 * component then draws a golden pill at the top of Ozzie's profile
 * with the exact instruction ("Tap the ⋯ menu on any post to open
 * Report + Block").
 *
 * Persistent until manually dismissed. Auto-cleans the query param on
 * dismiss so the URL stays clean if the reviewer bookmarks it.
 *
 * Delete this file + the one call site in App.js and the coach-marks
 * disappear. Zero effect on any non-reviewer user.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, X, ArrowDown } from 'lucide-react';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { isReviewerAccount } from '../../lib/reviewerMode';

const GUIDES = {
  report: {
    title: 'Report Objectionable Content',
    body: 'Tap the ⋯ menu on any post below → the Report sheet opens with 6 abuse categories. Submissions are moderated within 24 hours.',
    guideline: 'Guideline 1.2 · UGC',
    arrow: true,
  },
  block: {
    title: 'Manage Blocked Users',
    body: 'Tap Unblock next to any row to reverse a block. Blocking someone hides all their posts + prevents them from contacting you.',
    guideline: 'Guideline 1.2 · UGC',
    arrow: true,
  },
  delete: {
    title: 'Delete Account',
    body: 'Type "DELETE" in the modal, tap Delete Forever. This immediately wipes your profile, posts, and subscription data. Irreversible.',
    guideline: 'Guideline 5.1.1(v)',
    arrow: false,
  },
  paywall: {
    title: 'Subscription Paywall',
    body: 'All 5 tiers with monthly + annual pricing. Every tier includes a 7-day free trial. Restore Purchases link is in the footer.',
    guideline: 'Guideline 2.1 · IAP',
    arrow: false,
  },
  restore: {
    title: 'Restore Purchases',
    body: 'Scroll to the paywall footer — the "Restore Purchases" link asks StoreKit for any prior subscription tied to this Apple ID.',
    guideline: 'Guideline 3.1.1',
    arrow: true,
  },
  privacy: {
    title: 'Privacy Policy',
    body: 'Full disclosure of data collection, retention windows, third-party processors, and your GDPR/CCPA rights.',
    guideline: 'Guideline 5.1.1',
    arrow: false,
  },
  terms: {
    title: 'Terms of Service',
    body: 'Subscription terms, auto-renewal disclosure, EULA. Same terms shown at StoreKit checkout.',
    arrow: false,
  },
};

const ReviewerGuideBanner = () => {
  const { user } = useUserAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);

  const guideKey = searchParams.get('reviewer-guide');
  const guide = guideKey ? GUIDES[guideKey] : null;

  // Reset the dismiss flag whenever the guide key changes — otherwise
  // navigating from `?reviewer-guide=report` to `?reviewer-guide=block`
  // would keep the banner hidden.
  useEffect(() => { setDismissed(false); }, [guideKey]);

  if (!isReviewerAccount(user) || !guide || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    // Strip the query param so the URL is clean if the reviewer
    // shares/bookmarks it. Preserves every other search param.
    const next = new URLSearchParams(searchParams);
    next.delete('reviewer-guide');
    setSearchParams(next, { replace: true });
  };

  return (
    <div
      className="fixed left-0 right-0 z-[85] px-3 pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      data-testid="reviewer-guide-banner-wrapper"
    >
      <div
        className="mx-auto max-w-2xl rounded-2xl overflow-hidden text-black pointer-events-auto shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300"
        style={{
          background: 'linear-gradient(135deg, #FFD65A 0%, #F5A524 55%, #C77400 100%)',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 20px 40px -12px rgba(245,165,36,0.45), inset 0 1px 0 rgba(255,255,255,0.35)',
        }}
        data-testid="reviewer-guide-banner"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 mt-0.5"
            style={{
              background: 'rgba(0,0,0,0.14)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
            }}
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.8} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black leading-none opacity-80">
                Apple Review Guide
              </p>
              {guide.guideline && (
                <span className="text-[10px] font-black opacity-70">
                  · {guide.guideline}
                </span>
              )}
            </div>
            <p className="text-[14px] font-black tracking-tight leading-tight">
              {guide.title}
            </p>
            <p className="mt-1 text-[12px] font-semibold opacity-85 leading-snug">
              {guide.body}
            </p>
            {guide.arrow && (
              <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest opacity-80">
                <ArrowDown className="w-3.5 h-3.5 animate-bounce" strokeWidth={3} />
                Look below
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/10 transition flex-shrink-0"
            aria-label="Dismiss guide"
            data-testid="reviewer-guide-dismiss"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewerGuideBanner;
